import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle, KeyRound } from "lucide-react";
import { waLink } from "../lib/site";

const SKYLINE =
  "https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=2000&q=80";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-[#05070D]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={SKYLINE}
          alt="São Paulo skyline"
          className="w-full h-full object-cover opacity-40"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,7,13,0.85) 0%, rgba(5,7,13,0.55) 35%, rgba(5,7,13,0.92) 100%)",
          }}
        />
        {/* gold vignette */}
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-30"
          style={{
            background:
              "radial-gradient(circle, rgba(212,175,55,0.35) 0%, transparent 60%)",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-40 md:pt-44 pb-24 min-h-[100vh] flex flex-col justify-center">
        <div className="ag-reveal max-w-4xl">
          <div
            data-testid="hero-since-badge"
            className="inline-flex items-center gap-3 mb-6 pl-4 pr-5 py-2 border border-[#D4AF37]/50 bg-[#D4AF37]/[0.06] backdrop-blur-sm"
          >
            <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-pulse" />
            <span className="font-serif-ag text-[#F0CC59] text-sm tracking-[0.42em] font-medium">
              DESDE 2010
            </span>
            <span className="text-[10px] tracking-[0.24em] uppercase text-[#A3ABB8] hidden sm:inline">
              · Tradição & Confiança
            </span>
          </div>

          <div className="flex items-center gap-4 mb-8">
            <div className="gold-line" />
            <span className="overline">ASHIR GROUP — Capital Estratégico</span>
          </div>

          <h1 className="font-serif-ag text-white text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-light leading-[1.02] tracking-[-0.02em]">
            Capital imediato com{" "}
            <em className="text-[#D4AF37] not-italic" style={{ fontStyle: "italic" }}>
              juros reduzidos
            </em>{" "}
            e máxima segurança.
          </h1>

          <p className="mt-8 max-w-2xl text-lg md:text-xl text-[#A3ABB8] font-light leading-relaxed">
            Especialistas em cartas de consórcio contempladas para empresas,
            investidores e pessoas físicas. Tradição, credibilidade e soluções
            financeiras inteligentes.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#05070D] px-8 py-4 text-sm tracking-[0.18em] uppercase font-medium hover:bg-[#F0CC59] transition-all duration-300"
            >
              <MessageCircle size={17} strokeWidth={1.6} />
              Falar no WhatsApp
              <ArrowUpRight
                size={16}
                strokeWidth={1.6}
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </a>
            <a
              href="#contato"
              data-testid="hero-cta-analise"
              className="group inline-flex items-center justify-center gap-3 border border-[#D4AF37]/60 text-white px-8 py-4 text-sm tracking-[0.18em] uppercase font-light hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all duration-300"
            >
              Solicitar Análise
              <ArrowUpRight
                size={16}
                strokeWidth={1.4}
                className="text-[#D4AF37] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </a>
          </div>

          {/* NEXO promo pill */}
          <Link
            to="/nexo"
            data-testid="hero-nexo-cta"
            className="group mt-8 inline-flex items-center gap-3 pl-3 pr-5 py-2.5 border border-[#C8A25B]/50 bg-white/[0.02] hover:bg-[#C8A25B]/[0.08] hover:border-[#C8A25B] transition-all"
          >
            <span className="w-9 h-9 border border-[#C8A25B]/60 flex items-center justify-center">
              <KeyRound size={15} strokeWidth={1.5} className="text-[#C8A25B]" />
            </span>
            <span className="text-left leading-tight">
              <span className="block text-[9px] tracking-[0.3em] uppercase text-[#C8A25B] font-semibold">
                NEXO · Novo
              </span>
              <span className="block text-[13px] text-white/90 mt-0.5 font-light">
                Garantia para Consórcio{" "}
                <span className="text-[#C8A25B]">→</span>
              </span>
            </span>
          </Link>
        </div>

        {/* Bottom stats strip */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/5 bg-[#05070D]/60 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
            {[
              { k: "Desde 2010", v: "tradição no mercado" },
              { k: "100%", v: "segurança jurídica" },
              { k: "Nacional", v: "atendimento consultivo" },
              { k: "Itaim Bibi", v: "matriz em São Paulo" },
            ].map((s, i) => (
              <div key={i} className="py-6 px-4 md:px-6" data-testid={`hero-stat-${i}`}>
                <div className="font-serif-ag text-[#D4AF37] text-2xl md:text-3xl font-light">
                  {s.k}
                </div>
                <div className="text-[11px] md:text-xs uppercase tracking-[0.18em] text-[#A3ABB8] mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
